import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {USER_DATA} from 'user/user-prop-types';
import {connect} from 'react-redux';
import classNames from 'classnames';
import {compose} from 'redux';
import {v4 as uuidV4} from 'uuid';
import IconInfo from '@spothero/icons/info';
import Checkbox from '@spothero/ui/checkbox';
import Form from '@spothero/ui/form';
import FormElementError from '@spothero/ui/form-element-error';
import FormGroup from '@spothero/ui/form-group';
import HorizontalRule from '@spothero/ui/horizontal-rule';
import Label from '@spothero/ui/label';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import withConfig from 'config/withConfig';
import {push} from 'router/router-actions';
import {changeMultiRate} from 'spot/spot-actions';
import MultiRateChooser from 'spot-details/reservation-details/MultiRateChooser';
import PhoneNumber from 'user/phone/PhoneNumber';
import VehicleList from 'user/vehicle/VehicleList';
import VehicleColorSelect from 'user/vehicle/VehicleColorSelect';
import FormatUtils from 'utils/format';
import {showChangeDateTime, showRateDetails} from '../checkout-actions';
import {updateData} from 'search-request/search-request-actions';
import CheckoutUtils from '../checkout-utils';
import AfterPurchaseExplanationModal from './AfterPurchaseExplanationModal';
import AvailableReservations from './AvailableReservations';
import BillingFrequency from './BillingFrequency';

class MonthlyReservation extends Component {
    static propTypes = {
        isOpen: PropTypes.bool.isRequired,
        isMobile: PropTypes.bool.isRequired,
        user: PropTypes.shape(USER_DATA).isRequired,
        starts: PropTypes.string.isRequired,
        selectedRate: PropTypes.object.isRequired,
        monthlyRates: PropTypes.arrayOf(PropTypes.object).isRequired,
        transientAvailable: PropTypes.bool,
        facilityUrl: PropTypes.string.isRequired,
        phoneNumberRequired: PropTypes.bool,
        vehicleProfileId: PropTypes.number,
        vehicleColor: PropTypes.string,
        phoneNumber: PropTypes.string,
        phoneNumberFriendly: PropTypes.string,
        monthlyPhoneNumber: PropTypes.string,
        monthlyPhoneNumberFriendly: PropTypes.string,
        termsError: PropTypes.bool,
        smsParkingPass: PropTypes.bool,
        onMultiRateChange: PropTypes.func.isRequired,
        onChangeDateTimeClick: PropTypes.func.isRequired,
        onShowRateDetailsClick: PropTypes.func.isRequired,
        onUpdateMonthlyStarts: PropTypes.func.isRequired,
        onVehicleChange: PropTypes.func,
        pushTo: PropTypes.func.isRequired,
        currencyType: PropTypes.string,
        isLoadingRates: PropTypes.bool.isRequired,
    };
    state = {
        termsError: false,
        showAfterPurchaseModal: false,
        prevUser: this.props.user,
        vehicleListKey: uuidV4(),
    };

    constructor(props) {
        super(props);

        this._termsAgreed = false;
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {user, termsError} = nextProps;
        const {prevUser} = prevState;
        const newState = {};

        if (prevState.termsError !== termsError) {
            newState.termsError = termsError;
        }

        if (prevUser.status !== user.status || prevUser.email !== user.email) {
            newState.prevUser = user;
            newState.vehicleListKey = uuidV4();
        }

        return !isEmpty(newState) ? newState : null;
    }

    _onMultiRateChange = rid => {
        const {monthlyRates, onMultiRateChange} = this.props;

        onMultiRateChange({
            newRateRule: rid,
            rates: monthlyRates,
        });
    };

    _onReservationTermClick = () => {
        const {starts, pushTo, facilityUrl} = this.props;

        pushTo(CheckoutUtils.buildURLFromParams(facilityUrl, {starts}));
    };

    _onAfterPurchaseClick = evt => {
        evt.preventDefault();

        this.setState({
            showAfterPurchaseModal: true,
        });
    };

    _onAfterPurchaseHidden = () => {
        this.setState({
            showAfterPurchaseModal: false,
        });
    };

    _onTermsChange = checked => {
        this._termsAgreed = checked;

        this.setState({
            termsError: !checked,
        });
    };

    vehicleSelectForm() {
        // note: this returns a promise
        return this._vehicleSelectForm
            ? this._vehicleSelectForm.submit()
            : null;
    }

    get vehicleColorSelectForm() {
        return this._vehicleColorSelectForm;
    }

    get contractNameForm() {
        return this._contractNameForm;
    }

    get phoneForm() {
        return this._phoneForm;
    }

    get termsAgreed() {
        return this._termsAgreed;
    }

    render() {
        const {
            isOpen,
            isMobile,
            user,
            starts,
            selectedRate,
            monthlyRates,
            transientAvailable,
            phoneNumberRequired,
            vehicleProfileId,
            vehicleColor,
            smsParkingPass,
            phoneNumber,
            phoneNumberFriendly,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
            onChangeDateTimeClick,
            onShowRateDetailsClick,
            onVehicleChange,
            onUpdateMonthlyStarts,
            currencyType,
            isLoadingRates,
        } = this.props;
        const {termsError, showAfterPurchaseModal, vehicleListKey} = this.state;
        const {
            start_date_restrictions: startDateRestrictions,
            price_breakdown: priceBreakdown,
        } = selectedRate;
        const {
            startDateFormatted,
            endDateFormatted,
            invoiceStartDate,
            invoiceNextStartDate,
            invoiceStartDateFormatted,
            invoiceEndDateFormatted,
            invoiceNextStartDateFormatted,
            invoiceNextEndDateFormatted,
        } = CheckoutUtils.getMonthlyReservationInfo(selectedRate);
        const agreeCheckboxClasses = classNames('ReservationDetails-agree', {
            'FormElement-error': termsError,
        });
        const userHasVehicles = Boolean(user.vehicles && user.vehicles.length);
        const oversizeFee = CheckoutUtils.getOversizeFeeFromPriceBreakdown(
            priceBreakdown
        );

        return (
            <div className="MonthlyReservation">
                <div className="ReservationMonthly">
                    <h3>Rate Details</h3>
                    {monthlyRates.length > 1 ? (
                        <MultiRateChooser
                            selectedRate={selectedRate}
                            rates={monthlyRates}
                            onMultiRateChange={this._onMultiRateChange}
                            onInfoButtonClick={onShowRateDetailsClick}
                        />
                    ) : (
                        <div className="rate-details">
                            {selectedRate.title}
                            <TextButton
                                iconPosition="right"
                                onClick={onShowRateDetailsClick}
                            >
                                <IconInfo />
                            </TextButton>
                        </div>
                    )}
                    <h3>Requested Reservation</h3>
                    <p className="Reservation-Details-option">
                        {startDateFormatted} - {endDateFormatted}
                        <TextButton
                            className="change-datetime TextButton-pipe-before"
                            onClick={onChangeDateTimeClick}
                        >
                            Edit
                        </TextButton>
                        {transientAvailable && (
                            <TextButton
                                className="toggle-hourly-parking TextButton-pipe-before"
                                onClick={this._onReservationTermClick}
                            >
                                Hourly Parking Options
                            </TextButton>
                        )}
                    </p>
                    {startDateRestrictions !== 'none' &&
                        Boolean(invoiceNextStartDate) && (
                            <>
                                <h3>Available Reservations</h3>
                                <AvailableReservations
                                    updateMonthlyStarts={onUpdateMonthlyStarts}
                                    monthlyStarts={starts}
                                    invoiceStartDate={invoiceStartDate}
                                    invoiceNextStartDate={invoiceNextStartDate}
                                    invoiceStartDateFormatted={
                                        invoiceStartDateFormatted
                                    }
                                    invoiceEndDateFormatted={
                                        invoiceEndDateFormatted
                                    }
                                    invoiceNextStartDateFormatted={
                                        invoiceNextStartDateFormatted
                                    }
                                    invoiceNextEndDateFormatted={
                                        invoiceNextEndDateFormatted
                                    }
                                />
                            </>
                        )}
                    <h3>Billing Frequency</h3>
                    <BillingFrequency recurrable={selectedRate.recurrable} />
                    <TextButton
                        className="MonthlyReservation-after-purchase-toggle"
                        onClick={this._onAfterPurchaseClick}
                    >
                        What happens after I purchase?
                    </TextButton>
                    <HorizontalRule lineStyle="solid" />
                    {showAfterPurchaseModal && (
                        <AfterPurchaseExplanationModal
                            monthlyNumber={monthlyPhoneNumber || phoneNumber}
                            monthlyNumberFriendly={
                                monthlyPhoneNumberFriendly ||
                                phoneNumberFriendly
                            }
                            onHidden={this._onAfterPurchaseHidden}
                        />
                    )}
                </div>
                <h3>Vehicle Information</h3>
                <FormGroup>
                    <VehicleList
                        key={vehicleListKey}
                        ref={node => {
                            this._vehicleSelectForm = node;
                        }}
                        edit={isOpen}
                        user={user}
                        vehicleProfileId={vehicleProfileId}
                        showUnknownChoices={false}
                        showSaveAsDefault={false}
                        required
                        loadVehicleDataOnMount={userHasVehicles}
                        currencyType={currencyType}
                        oversizeFee={oversizeFee}
                        isLoadingRates={isLoadingRates}
                        onVehicleChange={onVehicleChange}
                    />
                </FormGroup>
                <FormGroup inline>
                    <VehicleColorSelect
                        ref={node => {
                            this._vehicleColorSelectForm = node;
                        }}
                        useLabels={!isMobile}
                        vehicleColor={vehicleColor}
                    />
                </FormGroup>
                <h3>Contact Information</h3>
                <FormGroup className="ReservationDetails-contact-info" inline>
                    <Form
                        ref={node => {
                            this._contractNameForm = node;
                        }}
                        id="MonthlyReservation-contract-name-form"
                    >
                        <TextInput
                            name="contractFullName"
                            className="ReservationDetails-contract-name"
                            label={<Label>Parker Full Name</Label>}
                            placeholder={!isMobile ? null : 'Parker Full Name'}
                            error={
                                <FormElementError>
                                    Please enter the parker&apos;s full name.
                                </FormElementError>
                            }
                            required
                        />
                    </Form>
                    <PhoneNumber
                        ref={node => {
                            this._phoneForm = node;
                        }}
                        phoneNumberRequired={phoneNumberRequired}
                        phone={
                            user.phoneNumber
                                ? FormatUtils.phone(user.phoneNumber)
                                : null
                        }
                        smsParkingPass={smsParkingPass}
                    />
                </FormGroup>
                {selectedRate.terms_and_conditions && (
                    <FormGroup>
                        <Checkbox
                            id="MonthlyReservation-terms-agree"
                            name="agree"
                            className={agreeCheckboxClasses}
                            onChange={this._onTermsChange}
                            label={
                                <Label>
                                    I have read and agree to the parking
                                    facility&apos;s{' '}
                                    <a
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={selectedRate.terms_and_conditions}
                                    >
                                        Terms &amp; Conditions
                                    </a>
                                </Label>
                            }
                        />
                        {termsError && (
                            <FormElementError>
                                You must accept the parking facility&apos;s
                                Terms &amp; Conditions to complete this
                                reservation.
                            </FormElementError>
                        )}
                    </FormGroup>
                )}
                <p className="AccountDetails-after-purchase">
                    After you check out, your parking pass will be emailed to
                    you as well as available in your account. Make sure to read
                    and follow the How To Redeem instructions on your parking
                    pass before your reservation start date, then you are free
                    to park. If you have any questions give us a call at{' '}
                    <a href={`tel:${monthlyPhoneNumber}`}>
                        {monthlyPhoneNumberFriendly}
                    </a>
                    .
                </p>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            phoneNumberRequired,
            smsParkingPass,
            vehicleProfileId,
            vehicleColor,
            currencyType,
        },
        city: {
            data: {
                phone_number: phoneNumber,
                phone_number_friendly: phoneNumberFriendly,
                monthly_phone_number: monthlyPhoneNumber,
                monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
            },
        },
        searchRequest: {starts},
        spot: {
            selected: {
                selectedRate,
                monthly_rates: monthlyRates,
                facility: {
                    transient_available: transientAvailable,
                    facility_url: facilityUrl,
                },
            },
        },
        user: {data: userData},
    } = state;

    return {
        user: userData,
        starts,
        selectedRate,
        monthlyRates,
        transientAvailable,
        facilityUrl,
        phoneNumberRequired,
        smsParkingPass,
        vehicleProfileId,
        vehicleColor,
        phoneNumber,
        phoneNumberFriendly,
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly,
        currencyType,
    };
};

const mapDispatchToProps = {
    onMultiRateChange: changeMultiRate,
    onChangeDateTimeClick: showChangeDateTime,
    onShowRateDetailsClick: showRateDetails,
    onUpdateMonthlyStarts: updateData,
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})
);

export default enhance(MonthlyReservation);
